<template>

  <div>

    <!-- Common error message -->
    <div v-if="error">
      <div class="alert alert-danger text-center">
        {{ error }}
      </div>
    </div>

    <div v-if="contactsLoading" class="text-center">
      <!-- Loading indicator -->
      <i class="fa fa-spinner fa-spin fa-3x"></i>
    </div>
    <div v-else>

      <!-- Contact list -->
      <div v-if="contacts.length" class="table-responsive">

        <!-- Filters -->
        <div class="form-row m-b-xxs m-t-xxs">

          <!-- By type -->
          <div class="col-auto my-1">
            <label>Type:</label>
            <select class="form-control" v-model="filters.contact_type_id.value">
              <option v-for="option in contactTypeOptions" :value="option.value">{{ option.label }}</option>
            </select>
          </div>

          <!-- By name -->
          <div class="col-auto my-1">
            <label>Name:</label>
            <input type="text" class="form-control" v-model="filters.full_name.value">
          </div>

          <!-- By address -->
          <div class="col-auto my-1">
            <label>Address:</label>
            <input type="text" class="form-control" v-model="filters.address.value">
          </div>

          <!-- By w9 -->
          <div class="col-auto my-1" v-if="showW9">
            <label>W9 on File:</label>
            <select class="form-control" v-model="filters.w9_on_file.value">
              <option v-for="option in booleanOptions" :value="option.value">{{ option.label }}</option>
            </select>
          </div>

          <div class="col-auto my-1">
            <label>&nbsp;</label>
            <button class="btn btn-sm btn-default form-control" value="clear" @click="resetFilters()">Clear</button>
          </div>

          <div class="col-auto my-1" v-if="allowCreateContact">
            <label>&nbsp;</label>
            <button class="btn btn-sm btn-primary form-control" @click="$modal.show('addContactModal');">
              <i class="fa fa-fw fa-user-plus"></i> Add
            </button>
          </div>
        </div>

        <hr class="m-t-sm m-b-sm">

        <table class="table table-bordered table-hover m-b-xs m-t-xs">

          <thead>
            <tr>
              <th>Photo</th>
              <th><a href="#" @click="sortBy('full_name')">Name<span :class="sortIconClass('full_name')"></span></a></th>
              <th><a href="#" @click="sortBy('type_title')">Type<span :class="sortIconClass('type_title')"></span></a></th>
              <th><a href="#" @click="sortBy('address')">Address<span :class="sortIconClass('address')"></span></a></th>
              <th v-if="showW9"><a href="#" @click="sortBy('w9_on_file')">W9 on File<span :class="sortIconClass('w9_on_file')"></span></a></th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="(contact, index) in filteredContacts">

              <td class="first-col">
                <button class="btn btn-white btn-sm" @click="$modal.show('contactView' + index);">
                  <img alt="image" class="rounded-circle" :src="photoLink(contact.photo)">
                </button>
              </td>

              <td>
                <a href="#" @click="$modal.show('contactView' + index);">
                  {{contact.full_name}}
                </a>
              </td>

              <td>{{contact.type_title}}</td>

              <td>
                {{ contact.address }}
                <i v-if="contact.city || contact.state"><br>{{ contact.city ? contact.city + ',' : '' }} {{ contact.state }} {{ contact.zip }}</i>
              </td>

              <td v-if="showW9">{{contact.w9_on_file ? 'Yes' : '-'}}</td>

              <td>
                <div class="btn-group btn-group-sm">
                  <button class="btn btn-primary" v-if="contactEditionAllowed(contact)" @click="$modal.show('contactEdit' + index);"><i class="fa fa-pencil"></i> Edit</button>
                  <button class="btn btn-primary" v-else-if="allowEditContact" disabled><i class="fa fa-pencil"></i> Edit</button>

                  <div v-if="contact.is_real_type && ((allowRemoveContact && contact.is_shared_type) || allowDeleteContact)" class="btn-group btn-group-sm">
                    <button type="button" class="btn btn-default dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                      <span class="caret"></span>
                    </button>
                    <div class="dropdown-menu">
                      <a class="dropdown-item" href="#" v-if="allowRemoveContact && contact.is_shared_type" @click="confirmRemoveContact(contact)">Remove from claim</a>
                      <a class="dropdown-item" href="#" v-if="allowDeleteContact" @click="confirmDeleteContact(contact)">Delete contact</a>
                    </div>
                  </div>
                </div>
              </td>

            </tr>
          </tbody>

        </table>

        <div v-if="!filteredContacts.length">
          <div class="alert alert-warning text-center m-t-xs m-b-xs">
            No contacts found
          </div>
        </div>

        <!-- Contact modals -->
        <div v-for="(contact, index) in contacts">

          <contact-view :modal-id="'contactView' + index"
                        :show-w9="showW9"
                        v-model="contacts[index]"></contact-view>

          <contact-form :modal-id="'contactEdit' + index"
                        title="Edit contact"
                        :claim-id="claimId"
                        :contact-id="contact.id"
                        :initial-contact="contact"
                        :states="states"
                        :contact-methods="contactMethods"
                        :contact-types="contactTypes"
                        :phone-placeholder="phonePlaceholder"
                        :phone-mask="phoneMask"
                        :show-w9="showW9"
                        :allow-attach-contact="allowAttachContact"
                        :allow-edit-w9="allowEditW9"
                        :allow-edit-optional-w9-contact-only="allowEditOptionalW9ContactOnly"
          ></contact-form>
        </div>

      </div>

      <div v-else>
        <div class="alert alert-info text-center m-t-xs m-b-xs">
          The claim has no related contacts.
        </div>
      </div>

    </div>

  </div>

</template>

<script>
  import ContactView from './ContactView'
  import ContactForm from './ContactForm'

  export default {

    /**
     * Input properties
     */
    props: {

      // Claim id
      claimId: {
        type: Number,
        required: true
      },

      // Show "W9 on file" marker.
      showW9: {
        type: Boolean,
        default: false
      },

      // Allow edit "W9 on file" on contact forms.
      allowEditW9: {
        type: Boolean,
        default: false
      },

      // Allow attach existing contacts.
      allowAttachContact: {
        type: Boolean,
        default: false
      },

      // Allow create contacts.
      allowCreateContact: {
        type: Boolean,
        default: false
      },

      // Allow edit contacts.
      allowEditContact: {
        type: Boolean,
        default: false
      },

      // Is user allowed to edit only a contact of type that not requires W9.
      allowEditOptionalW9ContactOnly: {
        type: Boolean,
        default: false
      },

      // Allow remove (detach) contacts.
      allowRemoveContact: {
        type: Boolean,
        default: false
      },

      // Allow delete contacts.
      allowDeleteContact: {
        type: Boolean,
        default: false
      },

      // Contact types list
      contactTypes: {
        type: [Array, Object],
        default: () => []
      },

      // US states list
      states: {
        type: [Array, Object],
        default: () => []
      },

      // Contact methods list
      contactMethods: {
        type: [Array, Object],
        default: () => []
      },

      // Phone mask
      phoneMask: {
        type: Object,
        default: null
      },

      // Phone placeholder
      phonePlaceholder: {
        type: String,
        default: null
      },
    },

    /**
     * Used components
     */
    components: {
      ContactView,
      ContactForm
    },

    /**
     * Variables (models).
     */
    data () {
      return {

        // Contact list.
        contacts: [],

        // Default filters.
        defaultFilters: {
          contact_type_id: {value: 'all', type: 'match'},
          full_name: {value: '', type: 'contains'},
          address: {value: '', type: 'contains', alsoSearch: ['city', 'state', 'zip']},
          w9_on_file: {value: 'all', type: 'match'},
        },

        // Current filters.
        filters: {},

        // Sort criteria: 'name', 'type_title', 'address', 'w9_on_file'.
        sortedBy: 'name',

        // Sort direction: 'asc', 'desc', 'toggle'.
        sortedDirection: 'asc',

        // Common error message.
        error: '',

        // Contacts loading indicator.
        contactsLoading: false,

      }
    },

    /**
     * Computed values.
     */
    computed: {
      // Contact type filter options.
      contactTypeOptions() {
        let options = Object.entries(this.contactTypes).map((type, index) => {
          return {value: type[0], label: type[1].label};
        });
        options.unshift({value: 'all', label: 'All'});
        return options;
      },

      // Check if real contact types present in list.
      realTypesPresent() {
        return !!Object.entries(this.contactTypes).find((type) => {
          return !type[1].virtual_type;
        });
      },

      // Boolean filter options.
      booleanOptions() {
        return [
          {value: 'all', label: 'All'},
          {value: true, label: 'Yes'},
          {value: false, label: 'No'}
        ];
      },

      // Filtered contacts.
      filteredContacts() {
        let filteredItems = this.contacts;

        for (var filterName in this.filters) {
          if(this.filters.hasOwnProperty(filterName)) {

            let filterValue = this.filters[filterName].value;
            let filterType = this.filters[filterName].type;
            let filterAlsoSearch = this.filters[filterName].alsoSearch ? this.filters[filterName].alsoSearch : [];

            if (!((filterValue === 'all') || filterValue === '')) {
              filteredItems = filteredItems.filter(item => {

                if (filterType === 'contains') {
                  let searchValues = [];
                  if (item[filterName]) searchValues.push(item[filterName]);
                  if (filterAlsoSearch.length) {
                    filterAlsoSearch.forEach((value) => {
                      if (item[value]) searchValues.push(item[value]);
                    });
                  }
                  if (searchValues.length) {
                    let found = false;
                    searchValues.forEach((searchValue) => {
                      let value = searchValue.toLowerCase();
                      let search = filterValue.toLowerCase();
                      if (value.indexOf(search) > -1) found = true;
                    });
                    return found;
                  }
                }
                else {
                  if (item[filterName]) {
                    return item[filterName] == filterValue;
                  } else {
                    if (!filterValue) return true;
                  }
                }

              });
            }
          }
        }
        return filteredItems;
      },
    },

    /**
     * Functions (methods).
     */
    methods: {

      /**
       * Init component.
       */
      init() {
        // Initial load contacts.
        this.loadContacts();
        this.resetFilters();
      },

      /**
       * Load contacts.
       */
      loadContacts() {
        this.contactsLoading = true;
        axios.get('/claims/' + this.claimId + '/contacts/list')
          .then(response =>{
            this.contacts = response.data;
            this.sortBy(this.sortedBy, this.sortedDirection);
          })
          .catch(error =>{
            this.handleErrors(error, 'Unable to load contacts.')
          })
          .then(() =>{
            this.contactsLoading = false;
          });
      },

      /**
       *  Link to contact image.
       */
      photoLink(src) {
        return src ? '/storage/' + src : '/images/profile_small.png';
      },

      /**
       * Is specified contact allowed to be changed by user.
       */
      contactEditionAllowed(contact) {
        // If contact edition allowed in common.
        if (this.allowEditContact) {
          // Check W9 permission.
          if (this.contactTypes[contact.contact_type_id].require_w9) {
            if (this.allowEditOptionalW9ContactOnly) {
              return false;
            }
          }
          return true;
        }
        return false;
      },

      /**
       * Remove contact from the claim dialog.
       * (for shared contacts only)
       */
      confirmRemoveContact(contact) {
        this.$swal.fire({
          title: 'Remove contact from claim',
          text: "You are about to remove contact from claim's contact list. The contact will not be deleted and still may be added to claims.",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Remove'
        }).then((result) => {
          if (result.value) {
            this.detachContact(contact);
          }
        })
      },

      /**
       * Remove contact from the claim.
       * (for shared contacts only)
       */
      detachContact(contact) {
        const formData = new FormData();
        axios.post('/claims/' + this.claimId + '/contacts/' + contact.id + '/detach', formData)
          .then(response => {
            this.$swal.fire(
              'Removed from claim',
              'The contact has been removed from claim\'s contact list.',
              'success'
            );
            this.loadContacts();
          })
          .catch(error => {
            this.handleErrors(error, 'Unable to remove contact')
          });
      },

      /**
       * Delete contact dialog.
       */
      confirmDeleteContact(contact) {
        let text = 'You are about to delete contact from CIMS. ';
        if (contact.is_shared_type) text += 'It will be removed from current claim\'s contact list and other claims contact lists it was added to.';
        text += 'This action cannot be undone.';

        this.$swal.fire({
          title: 'Delete contact',
          text: text,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Delete'
        }).then((result) => {
          if (result.value) {
            this.deleteContact(contact);
          }
        })
      },

      /**
       * Delete contact.
       */
      deleteContact(contact) {
        const formData = new FormData();
        formData.append('_method', "DELETE");
        axios.post('/claims/' + this.claimId + '/contacts/' + contact.id, formData)
          .then(response => {
            this.$swal.fire(
              'Deleted',
              'The contact has been deleted.',
              'success'
            );
            this.loadContacts();
          })
          .catch(error => {
            this.handleErrors(error, 'Unable to remove contact')
          });
      },

      /**
       * Sort contacts by specified column/direction.
       */
      sortBy(by, direction = 'toggle') {
        if (direction === 'toggle') {
          if (by === this.sortedBy) {
            direction = this.sortedDirection === 'asc' ? 'desc' : 'asc';
          } else direction = 'asc';
        }

        if (direction === 'asc') {
          this.contacts.sort((a, b) => ((a[by] ? a[by] : '') > (b[by] ? b[by] : '')) ? 1 : -1);
        } else {
          this.contacts.sort((a, b) => ((a[by] ? a[by] : '') > (b[by] ? b[by] : '')) ? -1 : 1);
        }

        this.sortedBy = by;
        this.sortedDirection = direction;
      },

      /**
       * Sort icon class to show near the column name.
       */
      sortIconClass(column) {
        return this.sortedBy === column ? 'fa fa-sort-' + this.sortedDirection : '';
      },

      /**
       * Reset filters.
       */
      resetFilters() {
        this.filters = _.cloneDeep(this.defaultFilters);
      },

      /**
       * Handle axios errors
       *
       * @param errorObj
       * @param defaultMessage
       */
      handleErrors(errorObj, defaultMessage) {
        if (errorObj.response.status === 403) {
          this.error = defaultMessage + ': access denied.';
        } else {
          this.error = defaultMessage;
        }
      },

    },

    /**
     * Initialize component on mount.
     */
    mounted () {
      this.init();
    }

  }
</script>

<style scoped>
  .first-col {
    text-align: left;
    width: 32px;
  }
  .first-col img {
    width: 32px;
    height: 32px;
  }
  .btn-group {
    display: flex;
  }
  @media (max-width: 767px) {
    .table-responsive .dropdown-menu {
      position: static !important;
    }
  }
  @media (min-width: 768px) {
    .table-responsive {
      overflow: inherit;
    }
  }
</style>
